// v19.8 (v19.9 expanded) — Admin-only comp modal.
//
// Two independent grants in the same panel so Donnell never has to open
// DevTools:
//   1) Grant Unlimited Access — 999,999 AI credits + Producer Package
//      pass through 2100. Backed by POST /api/admin/grant-unlimited.
//   2) Grant Admin Access — flips is_admin=true on the target user.
//      Backed by POST /api/admin/grant-admin. Kept as a SEPARATE call
//      (not folded into grant-unlimited) so promoting a collaborator to
//      admin doesn't ALSO comp their credits and vice-versa.
//
// Rendered ONLY when the parent (ProfileCircle) determines the current
// user is an admin OR the platform owner (Donnell). See ProfileCircle
// for the `showAdminItems` safety-net logic that guards against a stale
// auth-context bundle after a fresh production deploy.
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { X, Gift, ShieldStar, CheckCircle, WarningCircle, SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sub-form used for BOTH grants. `endpoint` and copy differ, everything
// else (email → POST → success/error banner) is identical, so pulling
// the shared shell out keeps the two flows in lock-step.
function GrantForm({
  testidPrefix,
  endpoint,
  title,
  icon,
  accent,
  description,
  placeholder,
  submitLabel,
  renderSuccessDetails,
  buttonBg,
}) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    const target = email.trim();
    if (!target) return;
    setBusy(true);
    setResult(null);
    try {
      const token = localStorage.getItem("crow_token");
      const { data } = await axios.post(
        `${API}${endpoint}`,
        { email: target },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult({
        ok: true,
        message: `${title} granted to ${data.email}`,
        user: data.user,
        changed: data.changed || [],
      });
      toast.success(`${title} · ${data.email}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let message;
      if (status === 404) message = `No user found with email "${target}"`;
      else if (status === 403) message = "You must be logged in as an admin.";
      else if (status === 401) message = "Session expired — please sign in again.";
      else message = detail || err?.message || "Grant failed. Try again.";
      setResult({ ok: false, message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="font-display font-bold text-base tracking-wide" style={{ color: accent }}>{title}</h3>
      </div>
      <p className="text-[11px] text-white/60 leading-relaxed">{description}</p>
      <label className="block">
        <div className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-1">User email</div>
        <input
          ref={inputRef}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          disabled={busy}
          className="w-full px-3 py-2.5 rounded-md bg-[#0a0a0a] border border-[#262626] text-sm font-mono focus:outline-none"
          style={{ ["--tw-ring-color"]: accent }}
          onFocus={(e) => { e.currentTarget.style.borderColor = accent; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = "#262626"; }}
          data-testid={`${testidPrefix}-email`}
          autoComplete="off"
        />
      </label>
      <button
        type="submit"
        disabled={busy || !email.trim()}
        className="w-full h-11 rounded-md flex items-center justify-center gap-2 font-bold text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ background: buttonBg, color: "#0a0a0a", boxShadow: `0 0 0 1px ${accent}55, 0 4px 12px ${accent}40` }}
        data-testid={`${testidPrefix}-submit`}
      >
        {busy ? (
          <><SpinnerGap size={16} weight="bold" className="animate-spin" /> Granting…</>
        ) : (
          <>{icon} {submitLabel}</>
        )}
      </button>
      {result && (
        <div
          className={`rounded-md border p-3 text-xs leading-relaxed ${
            result.ok
              ? "border-[#10b98155] bg-[#10b98111] text-[#a7f3d0]"
              : "border-[#ef444455] bg-[#ef444411] text-[#fca5a5]"
          }`}
          data-testid={result.ok ? `${testidPrefix}-success` : `${testidPrefix}-error`}
        >
          <div className="flex items-start gap-2">
            {result.ok
              ? <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0" />
              : <WarningCircle size={16} weight="fill" className="mt-0.5 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[13px] mb-1 text-white">{result.message}</div>
              {result.ok && result.user && renderSuccessDetails && (
                <div className="space-y-0.5 text-[11px] text-white/70 font-mono">
                  {renderSuccessDetails(result.user)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

export default function AdminGrantUnlimitedModal({ open, onClose }) {
  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      data-testid="admin-grant-modal"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md rounded-xl border border-[#2a2a2a] bg-[#0f0f0f] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.9)] p-6 text-white max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Gift size={20} weight="bold" className="text-[#34d399]" />
            <h2 className="font-display font-extrabold text-lg tracking-wide">Admin Controls</h2>
          </div>
          <button
            className="w-8 h-8 rounded flex items-center justify-center text-white/60 hover:bg-[#1a1a1a] hover:text-white"
            onClick={onClose}
            data-testid="admin-grant-close"
            title="Close (Esc)"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <GrantForm
          testidPrefix="admin-grant"
          endpoint="/admin/grant-unlimited"
          title="Grant Unlimited Access"
          icon={<Gift size={16} weight="bold" />}
          accent="#34d399"
          buttonBg="linear-gradient(180deg,#10b981,#059669)"
          description={
            <>Comps <span className="text-white/90 font-semibold">999,999 AI credits</span> + a{" "}
              <span className="text-white/90 font-semibold">Producer Package pass</span> valid through 2100.
              Idempotent — safe to re-run.</>
          }
          placeholder="hashimsattar066@gmail.com"
          submitLabel="Grant Unlimited Access"
          renderSuccessDetails={(u) => (
            <>
              <div>id: {u.id}</div>
              <div>credits: {u.credits}</div>
              <div>producer_active: {String(u.producer_subscription_active)}</div>
              <div>expires: {u.daw_access_expires_at}</div>
            </>
          )}
        />

        <div className="my-5 h-px bg-[#1f1f1f]" />

        <GrantForm
          testidPrefix="admin-grant-admin"
          endpoint="/admin/grant-admin"
          title="Grant Admin Access"
          icon={<ShieldStar size={16} weight="bold" />}
          accent="#f59e0b"
          buttonBg="linear-gradient(180deg,#fbbf24,#f59e0b)"
          description={
            <>Promotes the target user to <span className="text-white/90 font-semibold">is_admin = true</span>.
              Grants access to the Training Lab, admin dashboard, and this modal. Does NOT change credits.</>
          }
          placeholder="collaborator@example.com"
          submitLabel="Grant Admin Access"
          renderSuccessDetails={(u) => (
            <>
              <div>id: {u.id}</div>
              <div>is_admin: {String(u.is_admin)}</div>
              <div>granted_at: {u.admin_granted_at}</div>
            </>
          )}
        />
      </div>
    </div>,
    document.body,
  );
}
